<template>
	<v-row class="full-height">
		<v-col cols="8" class="flex-auto mx-auto">
			<div v-if="uploadedFileRates.length == 0" class="settings-actions">
				<!-- Search Field -->
				<v-text-field
					v-model="searchTerm"
					label="Search"
					placeholder="ZIP (90001) or State (CA)"
					prepend-inner-icon="mdi-magnify"
					clearable
					outlined
					dense
					hide-details
					class="search-field"
					@input="onSearchInput"
					@click:clear="onSearchClear"
				></v-text-field>

				<v-menu v-model="showUploadMenu" :close-on-content-click="false" offset-x left>
					<template v-slot:activator="{ on, attrs }">
						<v-btn depressed icon v-bind="attrs" v-on="on">
							<v-icon>mdi-upload</v-icon>
						</v-btn>
					</template>

					<v-card class="settings-actions-fileupload">
						<p>Rows should have 3 columns: ZIP Code, State, Combined Rate</p>
						<p class="text-center">Example: 90001,CA,9.5</p>
						<p>Any rows with non-digit zip or less than 3 columns will be ignored.</p>

						<div class="settings-actions-fileupload-input">
							<label v-if="uploadedFile" class="form-upload-file">
								{{ uploadedFile.name }}
								<v-icon @click.prevent="uploadedFile = null">mdi-close</v-icon>
							</label>
							<label v-else for="file-input" class="form-upload" @dragover.prevent @drop.stop.prevent="onFileUpload">
								<input type="file" id="file-input" @change="onFileUpload" style="display: none" />
								<v-icon>mdi-upload</v-icon>
								Drop or select a file
							</label>

							<p v-if="uploadError" class="settings-actions-fileupload-input-error">Please Make sure the file you are uploading is a .csv file.</p>
						</div>

						<v-card-actions class="settings-actions-fileupload-actions">
							<v-btn
								outlined
								depressed
								@click="
									showUploadMenu = false;
									uploadedFile = null;
								"
							>
								Cancel
							</v-btn>
							<v-btn color="primary" depressed @click="validateUploadedTaxRates()" :disabled="!uploadedFile"> Validate </v-btn>
						</v-card-actions>
					</v-card>
				</v-menu>

				<v-btn @click="downloadFile()" depressed icon>
					<v-icon>mdi-download</v-icon>
				</v-btn>
			</div>

			<div v-else class="settings-actions">
				<!-- Search Field (also shown during upload preview) -->
				<v-text-field
					v-model="searchTerm"
					label="Search"
					placeholder="ZIP (90001) or State (CA)"
					prepend-inner-icon="mdi-magnify"
					clearable
					outlined
					dense
					hide-details
					class="search-field"
					@input="onSearchInput"
					@click:clear="onSearchClear"
				></v-text-field>

				<v-btn
					outlined
					depressed
					@click="
						uploadedFile = null;
						uploadedFileRates = [];
					"
				>
					Cancel Upload
				</v-btn>
				<v-btn
					outlined
					depressed
					@click="
						uploadedFile = null;
						uploadedFileRates = [];
					"
				>
					Merge Changes
				</v-btn>
				<v-btn
					outlined
					depressed
					@click="
						uploadedFile = null;
						uploadedFileRates = [];
					"
				>
					Full Replacements
				</v-btn>
			</div>

			<div v-if="uploadedFileRates.length == 0" class="settings-table">
				<v-data-table
					:headers="headers"
					:items="zipCodeTaxRates"
					:options.sync="tableOptions"
					:server-items-length="totalCount"
					:loading="$apollo.queries.zipCodeTaxRates.loading"
					:footer-props="footerProps"
					class="zip-tax-rate-data-table"
				>
					<template v-slot:loading>
						<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
					</template>

					<template v-slot:no-data>
						<div class="text-center py-8">
							<v-icon size="64" color="grey lighten-1">mdi-map-marker</v-icon>
							<p class="mt-4 text-h6 grey--text">
								{{ searchTerm ? `No tax rates found for "${searchTerm}"` : 'No tax rates found' }}
							</p>
						</div>
					</template>

					<template v-slot:item.zipCode="{ item }">
						<span>{{ item.zipCode }}</span>
					</template>

					<template v-slot:item.state="{ item }">
						<span>{{ item.state }}</span>
					</template>

					<template v-slot:item.combinedRate="{ item }">
						<span>{{ formatRate(item.combinedRate) }}</span>
					</template>
				</v-data-table>
			</div>

			<template v-else>
				<div class="settings-table">
					<div class="settings-table-header">Uploaded File Contents</div>
					<div class="table">
						<div class="table-container">
							<table>
								<thead>
									<tr>
										<th>Zip Code</th>
										<th>State</th>
										<th>Combined Tax Rate</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(area, i) in uploadedFileRates" :key="i">
										<td>{{ area.zipCode }}</td>
										<td>{{ area.state }}</td>
										<td>{{ formatRate(area.combinedRate) }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</template>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import axios from "axios";
import RateFormattingMixin from "@/mixins/RateFormatting";
import { getApiUrl } from "@/helpers";
export default {
	name: "ZipTaxRate",
	mixins: [RateFormattingMixin],
	data() {
		return {
			zipCodeTaxRates: [],
			totalCount: 0,
			showUploadMenu: false,
			uploadError: false,
			uploadedFile: null,
			renderedFileContents: null,
			uploadedFileRates: [],
			searchTerm: "",
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: [],
				sortDesc: [],
			},
			headers: [
				{ text: "ZIP Code", value: "zipCode", width: "30%" },
				{ text: "State", value: "state", width: "30%" },
				{ text: "Combined Tax Rate", value: "combinedRate", width: "40%" },
			],
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
			},
		};
	},
	apollo: {
		zipCodeTaxRates: {
			query: gql`
				query GetZipCodeTaxRates($pageOffset: Int!, $take: Int!, $where: ZipCodeTaxRateFilterInput, $order: [ZipCodeTaxRateSortInput!]) {
					zipCodeTaxRates(skip: $pageOffset, take: $take, where: $where, order: $order) {
						items {
							zipCode
							state
							combinedRate
						}
						pageInfo {
							hasNextPage
							hasPreviousPage
						}
						totalCount
					}
				}
			`,
			variables() {
				const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;

				// Build the where clause based on search term
				let whereClause = null;
				if (this.searchTerm && this.searchTerm.trim()) {
					const term = this.searchTerm.trim();
					const termUpper = term.toUpperCase();

					// Check if it looks like a state code (2 letters) or ZIP code
					if (term.length === 2 && /^[A-Za-z]{2}$/.test(term)) {
						// Exact state search
						whereClause = {
							state: {
								eq: termUpper
							}
						};
					} else if (/^\d+$/.test(term)) {
						// Numeric only - search by ZIP code prefix
						whereClause = {
							zipCode: {
								startsWith: term
							}
						};
					} else {
						// Mixed or uncertain - search both ZIP and state using OR
						whereClause = {
							or: [
								{
									zipCode: {
										contains: term
									}
								},
								{
									state: {
										contains: termUpper
									}
								}
							]
						};
					}
				}

				// Build order clause
				let order;
				if (sortBy.length > 0) {
					const direction = sortDesc[0] ? "DESC" : "ASC";
					order = [{ [sortBy[0]]: direction }];
				} else {
					// Default sort by zipCode when no sort specified
					order = [{ zipCode: "ASC" }];
				}

				return {
					pageOffset: (page - 1) * itemsPerPage,
					take: itemsPerPage,
					where: whereClause,
					order,
				};
			},
			update(data) {
				this.totalCount = data.zipCodeTaxRates.totalCount || 0;
				return data.zipCodeTaxRates.items;
			},
			notifyOnNetworkStatusChange: true,
			debounce: 400,
		},
	},
	methods: {
		onSearchInput() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}

			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
			}, 400);
		},

		onSearchClear() {
			this.searchTerm = "";
			this.tableOptions.page = 1;
		},

		downloadFile() {
			const a = document.createElement("a");
			a.href = getApiUrl("/api/settings/zipTaxRates");
			a.download = a.href.split("/").pop();
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		},
		onFileUpload(event) {
			//assign the selected upload file via click or drag event to state
			this.uploadedFile = event.type == "change" ? event.target.files[0] : event.dataTransfer.files[0];

			//check file upload is csv type
			if (!this.uploadedFile.type.includes("csv")) {
				this.uploadedFile = null;
				this.uploadError = true;
				setTimeout(() => {
					this.uploadError = false;
				}, 2000);
				return;
			}

			//read contents of uploaded file and render the data as a string to the uploaded file contents
			let reader = new FileReader();
			reader.onload = (e) => {
				this.renderedFileContents = e.target.result;
			};
			reader.readAsText(this.uploadedFile);
			// reader.readAsDataURL(this.renderedFileContents);
		},

		async validateUploadedTaxRates() {
			try {
				//get each category from the renderedFileContents string and turn it into an array
				var formData = new FormData();
				formData.append("file", this.uploadedFile);
				// Let axios automatically set the correct Content-Type with boundary for FormData
				const response = await axios.put(getApiUrl("/api/settings/zipTaxRates"), formData);

				// Show success message if provided
				if (response.data?.message) {
					console.info('Upload result:', response.data.message);
					// You could also display this in a toast/snackbar if available
					alert(response.data.message);
				}

				this.showUploadMenu = false;
				this.$apollo.queries.zipCodeTaxRates.refetch();
			} catch (error) {
				console.error('Error uploading tax rates:', error);
				alert(error.response?.data?.error || 'Failed to upload tax rates. Please try again.');
				// Reset upload state on error
				this.uploadedFile = null;
				this.uploadedFileRates = [];
			}
		},
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.settings {
	&-actions {
		position: relative;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		width: 100%;
		margin-bottom: 1rem;
		gap: 1rem;

		.search-field {
			flex: 1;
			max-width: 350px;
			margin: 0;

			::v-deep .v-input__control {
				min-height: 36px;
			}

			::v-deep .v-input__slot {
				min-height: 36px;
				padding: 0 12px;
			}

			::v-deep .v-text-field__details {
				display: none;
			}
		}

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			margin-left: 0;
			height: 36px !important;
			min-height: 36px !important;

			&.v-btn--icon {
				background-color: $color-gray-light-2;
				flex-shrink: 0;
				width: 36px !important;
			}

			&:not(.v-btn--icon) {
				padding: 0 16px;
			}
		}

		&-fileupload {
			max-width: 51.5rem;
			padding: 2rem 2.2rem;

			p {
				padding-bottom: 0.5rem;
			}

			&-input {
				width: 100%;
				margin: 1.5rem 0rem 3rem;
				height: 18rem;

				&-error {
					color: $color-primary;
				}

				.form-upload {
					width: 100%;
					height: 100%;
				}
			}

			&-actions {
				display: flex;
				flex-flow: row wrap;
				justify-content: flex-end;
				padding: 0;
			}
		}
	}

	&-table {
		display: flex;
		flex-flow: column nowrap;
		width: 100%;
		height: 100%;

		&-header {
			display: block;
			padding: 1rem;
			font-size: 1.8rem;
		}

		.table {
			border: 1px solid $color-gray-dark;
		}
	}
}

.zip-tax-rate-data-table {
	border: 1px solid $color-gray;
	border-radius: 4px;

	::v-deep .v-data-table__wrapper {
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 600px;
		}
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
